<template>
	<div id="header">
		<img class="selectLogo" v-bind:src="testMode ? logoAlt : logo" />
		<dateSlider class="dateWrapper" :dateRangeValues="[minDate, maxDate]" ref="dateSlider" v-if="showDateSlider"/>
		<div class="buttonWrapper">
			<headerToggle title="Return comparison" storeRef="ManagerComparison" v-if="showManagers" />
			<download-excel :data="summaryData" :fields="jsonFields" class="download-wrapper" :name="csvName" type="csv" v-if="showManagers">
				<div class="text label">Download</div>
				<div class="icon-wrapper"><span class="icon-download"></span></div>
			</download-excel>
			<legendButton v-if="isLoaded" />
			<defineUniverse v-if="isLoaded" />
			<chartOptions v-if="isLoaded" />
			<div class="btn" @click="toggleTests()" v-if="showTestButton">{{ testModeText }}</div>
			<span
				class="icon-users"
				:class="disableCursor"
				@click="showManagerCards"
				v-show="showBasket"
				v-tooltip.left="'View selected manager cards'"
			>
				<span v-if="$store.state.selectedManagers.length > 0" class="selectedCount animated" :class="{ bounce: $store.state.ui.testAdded }">
					{{ $store.state.selectedManagers.length }}
				</span>
			</span>
			<span class="listIcon" @click="showTableView" v-if="showTable">
				<span class="icon-list" v-tooltip.left="'Back to tests table'"></span>
			</span>
			<span class="backToTestsIcon" @click="backToTests" v-if="showBasket">
				<span class="icon-arrow-left" v-tooltip.left="'Back to tests'"></span>
			</span>
			<span class="graphIcon" @click="showHomeView" v-if="showHome"> </span>
			<pinFunds v-if="isLoaded" />
		</div>
	</div>
</template>

<script>
import downloadExcel from 'vue-json-excel';
import moment from 'moment';
import chartOptions from '@/components/charts/chartOptions';
import dateSlider from '@/components/common/uiInputs/dateSlider';
import defineUniverse from '@/components/common/uiLayouts/defineUniverse';
import headerToggle from '@/components/common/uiInputs/toggle';
import legendButton from '@/components/common/uiLayouts/legend';
import logo from '@/assets/svg/select-logo.svg';
import logoAlt from '@/assets/svg/select-logo-alt.svg';
import search from '@/components/common/uiInputs/search';
import pinFunds from '@/components/common/uiLayouts/pinFunds';

export default {
    name: 'selectedHeader',
    data () {
        return {
            jsonData: [],
            jsonFields: {
                Name: 'Name',
                'Return (% - pa over 1y)': 'Return (% - pa over 1y)',
                'Standard deviation (% pa)': 'Standard deviation (% pa)',
                'Max drawdown': 'Max drawdown',
                AUM: 'AUM',
                Fee: 'Fee',
                'Track record': 'Length of track record',
            },
            logo,
            logoAlt,
            maxDate: '',
			minDate: ''
        };
    },
    props: ['summaryData'],
    components: {
        chartOptions,
        legendButton,
        dateSlider,
        defineUniverse,
        downloadExcel,
        headerToggle,
        pinFunds,
    },
    created () {
		this.minDate = this.moment(new Date(this.$store.state.ui.minimumDate)).format('DD/MM/YYYY');
		this.maxDate = this.moment(new Date(this.$store.state.ui.maximumDate)).format('DD/MM/YYYY');
        this.jsonData = this.summaryData;
    },
    methods: {
        showManagerCards () {
            if (this.$store.state.selectedManagers.length > 0) {
                this.$store.commit('ui/setNotFullscreen');
                this.$nextTick((i) => {
                    this.$store.commit('managerView');
                });
            }
        },
        showHomeView () {
            this.$store.commit('wipeManagers');
            this.$store.state.ui.dropdownSelected = false;
            this.$store.commit('ui/homeView');
        },
        showTableView () {
            this.$store.commit('backToTestsTable');
        },
        backToTests () {
            this.$store.commit('wipeManagers');
            this.$store.commit('chart/updateColourSelection', 'Asset class');
            this.$store.commit('ui/toggleTests');
        },
        moment (date) {
            return moment(date);
        },
        toggleTests () {
            this.$store.commit('removeManagers');
            this.$store.commit('chart/updateColourSelection', 'Asset class');
            this.$store.commit('resetValueFilter');
            this.$store.commit('ui/toggleTests');
            this.$store.commit('ui/toggleFullscreen');
            this.$store.commit('chart/updateChartType', 'bubble');
            this.$store.commit('chart/updateXaxisSelection', this.$store.state.universe.data.initialX);
            this.$store.commit('chart/updateYaxisSelection', this.$store.state.universe.data.initialY);
            this.$store.commit('chart/updateRaxisSelection', this.$store.state.universe.data.initialR);
            this.$store.commit('chart/updateColourSelection', this.$store.state.universe.data.initialColour);
        },
    },
    computed: {
        isLoaded () {
            return (
                this.$store.state.ui.pageState === 'loaded'
				&& (this.$store.state.ui.pageView === 'home' || this.$store.state.ui.pageView === 'search' || this.$store.state.ui.pageView === 'tests')
            );
        },
        showTestButton () {
            return this.isLoaded && this.$store.state.ui.pageView !== 'testTable';
        },
        showBasket () {
            return this.$store.state.ui.pageView === 'testTable';
        },
        showHome () {
            return this.isLoaded && this.pageView !== 'tests' && this.pageView !== 'testTable';
        },
        showTable () {
            return this.pageView === 'managers';
        },
        showDateSlider () {
            return this.$store.state.ui.pageState === 'loaded' && this.$store.state.ui.pageView !== 'testTable';
        },
        showManagers () {
            return (
                (this.$store.state.ui.pageState === 'loaded' && this.$store.state.ui.pageView === 'managers')
				|| this.$store.state.ui.pageView === 'ManagerComparison'
            );
        },
        disableCursor () {
            return this.$store.state.selectedManagers.length === 0 ? 'disabled' : '';
        },
        testModeText () {
            return this.testMode ? 'Exit Tests' : 'Start Tests';
        },
        csvName () {
            return `LCPSelect-SummaryResults-${moment().format('MMMMDoYYYY')}.xls`;
        },
    },
};
</script>

<style scoped lang="scss">
@import "src/assets/scss/all.scss";
#header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
	padding: 10px 10px 0 10px;
	.selectLogo {
		cursor: pointer;
		display: inline-block;
		width: 180px;
	}
	.download-wrapper {
		cursor: pointer;
		display: inline-block;
		height: 48px;
		margin: 0 10px;
		vertical-align: middle;
		width: 80px;
		.text {
			color: #595959;
			font-size: 12px;
			font-weight: 400;
			text-align: center;
		}
		.icon-wrapper {
			display: block;
			font-size: 20px;
			margin-left: auto;
			margin-right: auto;
			width: 20px;
		}
	}
	.icon-users,
	.icon-home,
	.icon-list,
	.icon-arrow-left {
		color: $midGrey;
		cursor: pointer;
		float: right;
		font-size: 36px;
		margin-right: 20px;
		padding-top: 10px;
		position: relative;
		vertical-align: middle;
		&.disabled {
			cursor: default;
		}
		.selectedCount {
			background-color: $lightBlue;
			border-radius: 10px;
			color: white;
			font-family: "Montserrat", sans-serif;
			font-size: 14px;
			height: 20px;
			left: -10px;
			padding-top: 2px;
			position: absolute;
			text-align: center;
			top: -3px;
			width: 20px;
			line-height: 18px;
		}
	}
	.buttonWrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}
}
</style>
